import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Grid3X3, Key, Navigation } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  polyalphabeticOperations: number;
  totalTablesUsed: number;
  uniqueKeys: Set<string>;
  averageKeyLength: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  key: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  keyRepeats: number;
}

const BeaufortCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [cipherKey, setCipherKey] = useState<string>('BEAUFORT');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'table' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(false);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(false);
  const [showTable, setShowTable] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(true);
  const [groupSize, setGroupSize] = useState<number>(5);
  const [autoKey, setAutoKey] = useState<boolean>(false);
  const [variant, setVariant] = useState<'standard' | 'variant'>('standard');
  const [keyIndicators, setKeyIndicators] = useState<boolean>(true);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    polyalphabeticOperations: 0,
    totalTablesUsed: 0,
    uniqueKeys: new Set<string>(),
    averageKeyLength: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [beaufortTable, setBeaufortTable] = useState<string[][]>([]);

  // Generate Beaufort table
  const generateBeaufortTable = useCallback((): string[][] => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const table: string[][] = [];
    
    for (let i = 0; i < 26; i++) {
      table[i] = [];
      for (let j = 0; j < 26; j++) {
        if (variant === 'standard') {
          // Standard Beaufort: Key - Plaintext mod 26
          table[i][j] = alphabet[(i - j + 26) % 26];
        } else {
          // Variant Beaufort: Plaintext - Key mod 26
          table[i][j] = alphabet[(j - i + 26) % 26];
        }
      }
    }
    
    return table;
  }, [variant]);

  // Generate extended key
  const generateExtendedKey = useCallback((text: string, key: string): string => {
    if (!key || !text) return '';
    
    const cleanText = text.replace(/[^A-Z]/g, '').toUpperCase();
    const cleanKey = key.replace(/[^A-Z]/g, '').toUpperCase();
    
    if (autoKey) {
      // Auto-key: use key + plaintext as extended key
      const extendedKey = cleanKey + cleanText;
      return extendedKey.slice(0, cleanText.length);
    } else {
      // Standard: repeat key
      let extendedKey = '';
      for (let i = 0; i < cleanText.length; i++) {
        extendedKey += cleanKey[i % cleanKey.length];
      }
      return extendedKey;
    }
  }, [autoKey]);

  // Prepare text for Beaufort cipher
  const prepareText = useCallback((text: string): string => {
    return text.replace(/[^A-Z]/g, '').toUpperCase();
  }, []);

  // Beaufort cipher encryption
  const beaufortEncrypt = useCallback((text: string, key: string): string => {
    if (!text || !key) return '';
    
    const table = generateBeaufortTable();
    setBeaufortTable(table);
    
    const preparedText = prepareText(text);
    const extendedKey = generateExtendedKey(preparedText, key);
    let result = '';
    
    // Process each character
    for (let i = 0; i < preparedText.length; i++) {
      const plainChar = preparedText[i];
      const keyChar = extendedKey[i];
      
      if (!keyChar) break;
      
      const plainIndex = plainChar.charCodeAt(0) - 65;
      const keyIndex = keyChar.charCodeAt(0) - 65;
      
      if (plainIndex >= 0 && plainIndex < 26 && keyIndex >= 0 && keyIndex < 26) {
        result += table[keyIndex][plainIndex];
      }
    }
    
    // Format output
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [generateBeaufortTable, prepareText, generateExtendedKey, groupOutput, groupSize]);

  // Beaufort cipher decryption
  const beaufortDecrypt = useCallback((text: string, key: string): string => {
    if (!text || !key) return '';
    
    const table = generateBeaufortTable();
    setBeaufortTable(table);
    
    // Remove spaces if text was grouped
    const cleanedText = text.replace(/\s/g, '');
    const preparedText = prepareText(cleanedText);
    
    let result = '';
    let extendedKey = '';
    
    // For auto-key decryption, we need to build the key as we decrypt
    if (autoKey) {
      const cleanKey = key.replace(/[^A-Z]/g, '').toUpperCase();
      extendedKey = cleanKey;
      
      for (let i = 0; i < preparedText.length; i++) {
        const cipherChar = preparedText[i];
        const keyChar = extendedKey[i];
        
        if (!keyChar) break;
        
        const cipherIndex = cipherChar.charCodeAt(0) - 65;
        const keyIndex = keyChar.charCodeAt(0) - 65;
        
        if (cipherIndex >= 0 && cipherIndex < 26 && keyIndex >= 0 && keyIndex < 26) {
          // Find plaintext character in the table
          let plainChar = '';
          for (let j = 0; j < 26; j++) {
            if (table[keyIndex][j] === cipherChar) {
              plainChar = String.fromCharCode(65 + j);
              break;
            }
          }
          result += plainChar;
          extendedKey += plainChar; // Add decrypted char to key
        }
      }
    } else {
      extendedKey = generateExtendedKey(preparedText, key);
      
      for (let i = 0; i < preparedText.length; i++) {
        const cipherChar = preparedText[i];
        const keyChar = extendedKey[i];
        
        if (!keyChar) break;
        
        const cipherIndex = cipherChar.charCodeAt(0) - 65;
        const keyIndex = keyChar.charCodeAt(0) - 65;
        
        if (cipherIndex >= 0 && cipherIndex < 26 && keyIndex >= 0 && keyIndex < 26) {
          // Find plaintext character in the table
          for (let j = 0; j < 26; j++) {
            if (table[keyIndex][j] === cipherChar) {
              result += String.fromCharCode(65 + j);
              break;
            }
          }
        }
      }
    }
    
    return result;
  }, [generateBeaufortTable, prepareText, generateExtendedKey, autoKey]);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    if (!cipherKey.trim()) {
      setOutputText('');
      return '';
    }
    
    const result = operation === 'encrypt' 
      ? beaufortEncrypt(inputText, cipherKey)
      : beaufortDecrypt(inputText, cipherKey);
    
    setOutputText(result);
    return result;
  }, [inputText, cipherKey, operation, beaufortEncrypt, beaufortDecrypt]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !cipherKey.trim() || !result) return;
    
    const cleanInput = inputText.replace(/[^A-Z]/g, '').toUpperCase();
    const keyRepeats = Math.ceil(cleanInput.length / cipherKey.replace(/[^A-Z]/g, '').length);
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      key: cipherKey,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      keyRepeats: keyRepeats
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newUniqueKeys = new Set(Array.from(prevStats.uniqueKeys).concat([cipherKey.toUpperCase()]));
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        polyalphabeticOperations: prevStats.polyalphabeticOperations + 1,
        totalTablesUsed: prevStats.totalTablesUsed + 1,
        uniqueKeys: newUniqueKeys,
        averageKeyLength: Math.round(Array.from(newUniqueKeys).reduce((sum, k) => sum + k.length, 0) / newUniqueKeys.size)
      };
    });
  }, [inputText, cipherKey, operation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && cipherKey) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, cipherKey, operation, autoKey, variant]);

  // Update table when variant changes
  useEffect(() => {
    setBeaufortTable(generateBeaufortTable());
  }, [generateBeaufortTable]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setCipherKey('BEAUFORT');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "ATTACK AT DAWN FROM THE EAST",
      "THE BEAUFORT CIPHER IS RECIPROCAL",
      "POLYALPHABETIC SUBSTITUTION WORKS",
      "NAVAL SIGNALS AND COMMUNICATION",
      "ADMIRALTY CODE BOOK SYSTEM",
      "CIPHER WHEEL ENCRYPTION METHOD",
      "CRYPTOGRAPHIC SECURITY ANALYSIS"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKey = () => {
    const words = [
      'BEAUFORT', 'NAVAL', 'CIPHER', 'SECRET', 'CRYPTO', 'SIGNAL',
      'ADMIRALTY', 'MARINE', 'ENCODE', 'DECODE', 'HIDDEN', 'SECURITY',
      'WHEEL', 'TABLE', 'POLYALPHA', 'RECIPROCAL'
    ];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setCipherKey(randomWord);
  };

  return (
    <>
      <Helmet>
        <title>Beaufort Cipher - Polyalphabetic Substitution Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Beaufort Cipher tool with polyalphabetic substitution, reciprocal encryption, and naval cryptography features. Perfect for classical cipher education and historical encryption." />
        <meta name="keywords" content="beaufort cipher, polyalphabetic cipher, reciprocal cipher, naval cipher, admiralty cipher, polyalphabetic substitution, classical cryptography, cipher wheel, naval signals, cryptography education" />
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Navigation className="text-indigo-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Beaufort Cipher</h1>
                <p className="text-sm opacity-60">Polyalphabetic Substitution Cipher (Naval Origins)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setActiveModal(activeModal === 'table' ? null : 'table')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Beaufort Table"
              >
                <Grid3X3 size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">⚓ Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Cipher Key</label>
                    <button
                      onClick={generateRandomKey}
                      className={`text-xs px-2 py-1 rounded ${
                        isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                      } text-white transition-colors`}
                      title="Generate Random Key"
                    >
                      Random
                    </button>
                  </div>
                  <input
                    type="text"
                    value={cipherKey}
                    onChange={(e) => setCipherKey(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
                    placeholder="Enter cipher key..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Variant</label>
                  <select
                    value={variant}
                    onChange={(e) => setVariant(e.target.value as 'standard' | 'variant')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="standard">Standard Beaufort</option>
                    <option value="variant">Variant Beaufort</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Key Information */}
              {keyIndicators && cipherKey && inputText && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Key Analysis:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                    <div>
                      <div className="font-medium mb-1">Key: {cipherKey}</div>
                      <div className="opacity-60">Length: {cipherKey.replace(/[^A-Z]/g, '').length} characters</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">Mode: {autoKey ? 'Auto-key' : 'Repeating key'}</div>
                      <div className="opacity-60">Variant: {variant === 'standard' ? 'Standard' : 'Variant'} Beaufort</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">Key Repeats: {Math.ceil(inputText.replace(/[^A-Z]/g, '').length / cipherKey.replace(/[^A-Z]/g, '').length)}</div>
                      <div className="opacity-60">Text Length: {inputText.replace(/[^A-Z]/g, '').length} chars</div>
                    </div>
                  </div>
                  
                  {/* Key visualization */}
                  <div className="mt-3">
                    <div className="text-xs font-medium mb-2">Extended Key Pattern:</div>
                    <div className="font-mono text-xs bg-gray-600 text-white p-2 rounded max-w-full overflow-x-auto">
                      {generateExtendedKey(inputText.replace(/[^A-Z]/g, '').toUpperCase(), cipherKey).slice(0, 50)}
                      {generateExtendedKey(inputText.replace(/[^A-Z]/g, '').toUpperCase(), cipherKey).length > 50 && '...'}
                    </div>
                  </div>
                </div>
              )}

              {/* Table Preview */}
              {showTable && beaufortTable.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Beaufort Table ({variant === 'standard' ? 'Standard' : 'Variant'}):</h4>
                  <div className="overflow-x-auto">
                    <div className="grid grid-cols-27 gap-px text-xs w-fit mx-auto">
                      {/* Headers */}
                      <div className="w-6 h-6"></div>
                      {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
                        <div key={letter} className={`w-6 h-6 flex items-center justify-center font-bold ${
                          isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
                        }`}>
                          {letter}
                        </div>
                      ))}
                      
                      {/* Table rows */}
                      {beaufortTable.slice(0, 5).map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                          <div className={`w-6 h-6 flex items-center justify-center font-bold ${
                            isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
                          }`}>
                            {String.fromCharCode(65 + rowIndex)}
                          </div>
                          {row.map((cell, colIndex) => (
                            <div
                              key={`${rowIndex}-${colIndex}`}
                              className={`w-6 h-6 flex items-center justify-center font-mono text-xs border ${
                                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            >
                              {cell}
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs opacity-60 mt-3 text-center">
                    {variant === 'standard' ? 'Standard Beaufort: C = K - P (mod 26)' : 'Variant Beaufort: C = P - K (mod 26)'} • 
                    Showing first 5 rows of 26×26 table
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Beaufort cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center space-x-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <Upload size={16} className="mr-1" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'beaufort-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Table Modal */}
        {activeModal === 'table' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-6xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Grid3X3 className="text-indigo-500" />
                  <span>Beaufort Cipher Table</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-sm opacity-60 mb-4">
                    {variant === 'standard' ? 'Standard' : 'Variant'} Beaufort Table • 
                    Formula: {variant === 'standard' ? 'C = K - P (mod 26)' : 'C = P - K (mod 26)'}
                  </div>
                </div>
                
                {/* Full Table Display */}
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-27 gap-1 w-fit mx-auto">
                    {/* Headers */}
                    <div className="w-8 h-8"></div>
                    {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
                      <div key={letter} className={`w-8 h-8 flex items-center justify-center text-sm font-bold border-2 ${
                        isDarkMode ? 'bg-indigo-700 border-indigo-600 text-white' : 'bg-indigo-100 border-indigo-300 text-indigo-900'
                      }`}>
                        {letter}
                      </div>
                    ))}
                    
                    {/* Table rows */}
                    {beaufortTable.map((row, rowIndex) => (
                      <React.Fragment key={rowIndex}>
                        <div className={`w-8 h-8 flex items-center justify-center text-sm font-bold border-2 ${
                          isDarkMode ? 'bg-indigo-700 border-indigo-600 text-white' : 'bg-indigo-100 border-indigo-300 text-indigo-900'
                        }`}>
                          {String.fromCharCode(65 + rowIndex)}
                        </div>
                        {row.map((cell, colIndex) => (
                          <div
                            key={`${rowIndex}-${colIndex}`}
                            className={`w-8 h-8 flex items-center justify-center text-sm font-mono font-bold border hover:bg-opacity-80 transition-colors cursor-pointer ${
                              isDarkMode ? 'bg-gray-600 border-gray-500 text-white hover:bg-gray-500' : 'bg-white border-gray-400 text-gray-900 hover:bg-gray-100'
                            }`}
                            title={`Key: ${String.fromCharCode(65 + rowIndex)}, Plain: ${String.fromCharCode(65 + colIndex)} → Cipher: ${cell}`}
                          >
                            {cell}
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                
                <div className="text-center space-y-4">
                  <div className="text-sm opacity-60 space-y-2">
                    <div><strong>How to use the table:</strong></div>
                    <div>• Find the key letter in the left column</div>
                    <div>• Find the plaintext letter in the top row</div>
                    <div>• The cipher letter is at their intersection</div>
                    <div>• Beaufort cipher is reciprocal (encryption = decryption)</div>
                  </div>
                  
                  {/* Example */}
                  <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-sm font-medium mb-2">Example:</div>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Key letter B, Plaintext A:</strong> Find row B, column A → Result: {beaufortTable[1]?.[0]}</div>
                      <div><strong>Key letter E, Plaintext L:</strong> Find row E, column L → Result: {beaufortTable[4]?.[11]}</div>
                      <div><strong>Formula ({variant}):</strong> {variant === 'standard' ? 'C = (K - P) mod 26' : 'C = (P - K) mod 26'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-indigo-500" />
                  <span>About Beaufort Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Beaufort Cipher?</h3>
                  <p>The Beaufort Cipher is a polyalphabetic substitution cipher that closely resembles the Vigenère cipher but uses a different encryption formula. Named after Sir Francis Beaufort (creator of the Beaufort wind scale), it was used by the British Admiralty for naval communications. Unlike Vigenère, the Beaufort cipher is reciprocal, meaning the same process is used for both encryption and decryption.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Create a 26×26 substitution table (Beaufort square)</li>
                    <li>Each row corresponds to a key letter</li>
                    <li>Use the formula: C = (K - P) mod 26 for standard Beaufort</li>
                    <li>Repeat the key to match the length of the plaintext</li>
                    <li>Apply the substitution for each character pair</li>
                    <li>The same process decrypts the ciphertext</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Standard vs Variant Beaufort</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="space-y-2">
                      <div><strong>Standard Beaufort:</strong></div>
                      <div className="font-mono text-xs">C = (K - P) mod 26</div>
                      <div><strong>Variant Beaufort:</strong></div>
                      <div className="font-mono text-xs">C = (P - K) mod 26</div>
                      <div className="text-xs opacity-60 mt-2">
                        Where C = Cipher, K = Key, P = Plaintext
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Background</h3>
                  <ul className="space-y-1">
                    <li><strong>Named after:</strong> Sir Francis Beaufort (1774-1857)</li>
                    <li><strong>Origin:</strong> British Royal Navy</li>
                    <li><strong>Period:</strong> 19th century naval communications</li>
                    <li><strong>Usage:</strong> Admiralty telegraphs and signal books</li>
                    <li><strong>Purpose:</strong> Secure naval and diplomatic messages</li>
                    <li><strong>Relationship:</strong> Close cousin to Vigenère cipher</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Reciprocal Property</h3>
                  <p>One of the most important features of the Beaufort cipher is its reciprocal nature. This means that the encryption and decryption processes are identical - if you encrypt a message with a key, you can decrypt it by applying the same process with the same key. This property made it particularly valuable for military use.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Reciprocal encryption/decryption process</li>
                    <li>Stronger than monoalphabetic substitution</li>
                    <li>Simple to implement with cipher wheels</li>
                    <li>Resistant to basic frequency analysis</li>
                    <li>Auto-key variant provides enhanced security</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Beaufort cipher provided good security for 19th-century naval communications but is easily broken by modern cryptanalytic techniques. While historically significant and educationally valuable, it should not be used for actual security purposes today.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Table</label>
                  <input
                    type="checkbox"
                    checked={showTable}
                    onChange={(e) => setShowTable(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Auto-Key Mode</label>
                  <input
                    type="checkbox"
                    checked={autoKey}
                    onChange={(e) => setAutoKey(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Key Indicators</label>
                  <input
                    type="checkbox"
                    checked={keyIndicators}
                    onChange={(e) => setKeyIndicators(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="range"
                      min="3"
                      max="10"
                      value={groupSize}
                      onChange={(e) => setGroupSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group: {groupSize}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Keys</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['BEAUFORT', 'NAVAL', 'ADMIRALTY', 'SIGNAL', 'CIPHER', 'SECRET', 'MARINE', 'SECURE', 'CRYPTO', 'ENCODE', 'DECODE', 'WINDS'].map((word, index) => (
                      <button
                        key={index}
                        onClick={() => setCipherKey(word)}
                        className={`p-2 text-xs rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Cipher Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setCipherKey('BEAUFORT');
                        setVariant('standard');
                        setAutoKey(false);
                        setGroupOutput(true);
                        setGroupSize(5);
                        setKeyIndicators(true);
                        setShowTable(true);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                      } transition-colors`}
                    >
                      Classical Naval
                    </button>
                    
                    <button
                      onClick={() => {
                        setCipherKey('ADMIRALTY');
                        setVariant('standard');
                        setAutoKey(true);
                        setGroupOutput(true);
                        setGroupSize(4);
                        setKeyIndicators(true);
                        setShowTable(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Auto-Key Enhanced
                    </button>
                    
                    <button
                      onClick={() => {
                        setCipherKey('SIGNAL');
                        setVariant('variant');
                        setAutoKey(false);
                        setGroupOutput(false);
                        setKeyIndicators(false);
                        setShowTable(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.polyalphabeticOperations}</div>
                  <div className="text-sm">Polyalphabetic Ops</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeys.size}</div>
                  <div className="text-sm">Unique Keys</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageKeyLength}</div>
                  <div className="text-sm">Avg Key Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.totalTablesUsed}</div>
                  <div className="text-sm">Tables Used</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.keyRepeats} repeats
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-indigo-500 text-white">
                            {item.key}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Navigation size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Most Used Key:</div>
                      <div>
                        {history.length > 0 
                          ? (() => {
                              const keyCounts = history.reduce((acc, item) => {
                                acc[item.key] = (acc[item.key] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>);
                              return Object.keys(keyCounts).reduce((a, b) => keyCounts[a] > keyCounts[b] ? a : b);
                            })()
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Avg Key Repeats:</div>
                      <div>{stats.totalOperations > 0 ? Math.round(history.reduce((sum, item) => sum + item.keyRepeats, 0) / history.length) : 0}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Polyalphabetic Efficiency:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.polyalphabeticOperations / stats.totalOperations) * 100) : 0}% Advanced</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BeaufortCipherApp;